import { createI18n } from 'vue-i18n'
import en from './locales/en.json'
import fr from './locales/fr.json'
import es from './locales/es.json'
import it from './locales/it.json'
import de from './locales/de.json'
import pt from './locales/pt.json'
import zh from './locales/zh.json'
import ja from './locales/ja.json'

// i18n configuration constants
export const LANGUAGE_STORAGE_KEY = 'vte-language'
export const LANGUAGE_URL_PARAM = 'lang'
export const defaultLanguage = 'en'
export const fallbackLanguage = 'en'
export const availableLanguages = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', dbId: 1 },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', dbId: 3 },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', dbId: 4 },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', dbId: 5 },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', dbId: 6 },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹', dbId: 7 },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', dbId: 8 },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', dbId: 9 },
] as const

export type SupportedLocale = 'en' | 'fr' | 'es' | 'it' | 'de' | 'pt' | 'zh' | 'ja'

/**
 * Check if a locale is supported
 */
export function isLocaleSupported(locale: string): boolean {
  return availableLanguages.some(lang => lang.code === locale)
}

/**
 * Get the initial locale for the viewer based on priority:
 * 1. URL parameter (?lang=fr) - highest priority for embedding
 * 2. localStorage saved preference
 * 3. Browser language
 * 4. Default language
 */
function getInitialLocale(): SupportedLocale {
  // 1. Check URL parameter (highest priority for viewer embedding)
  const urlParams = new URLSearchParams(window.location.search)
  const urlLang = urlParams.get(LANGUAGE_URL_PARAM)
  if (urlLang && isLocaleSupported(urlLang)) {
    return urlLang as SupportedLocale
  }

  // 2. Check localStorage
  const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY)
  if (saved && isLocaleSupported(saved)) {
    return saved as SupportedLocale
  }

  // 3. Check browser language
  const browserLang = navigator.language.split('-')[0]
  if (isLocaleSupported(browserLang)) {
    return browserLang as SupportedLocale
  }

  // 4. Default
  return defaultLanguage
}

/**
 * Create the i18n instance
 */
export const i18n = createI18n({
  legacy: false, // Use Composition API mode
  locale: getInitialLocale(),
  fallbackLocale: fallbackLanguage,
  messages: { en, fr, es, it, de, pt, zh, ja }
})

/**
 * Set the current locale and persist to localStorage
 */
export function setLocale(locale: SupportedLocale) {
  if (!isLocaleSupported(locale)) {
    console.warn(`Locale "${locale}" is not supported, falling back to "${defaultLanguage}"`)
    locale = defaultLanguage
  }

  i18n.global.locale.value = locale
  localStorage.setItem(LANGUAGE_STORAGE_KEY, locale)
  document.documentElement.setAttribute('lang', locale)
}

/**
 * Get the current locale
 */
export function getLocale(): SupportedLocale {
  return i18n.global.locale.value as SupportedLocale
}

/**
 * Check if the current language was set via URL parameter
 */
export function isLanguageFromUrl(): boolean {
  const urlParams = new URLSearchParams(window.location.search)
  const urlLang = urlParams.get(LANGUAGE_URL_PARAM)
  return urlLang !== null && isLocaleSupported(urlLang)
}
